import { OpenFeature, type EvaluationContext } from '@openfeature/web-sdk';
import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import { useSyncExternalStore } from 'react';
import type { User } from '@/lib/types';
import { useAuthStore } from '@/stores/auth-store';

const ENVIRONMENT_ID = import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID;
const APP_ENV = import.meta.env.VITE_APP_ENV;

let providerEnabled = false;
/** targetingKey whose context has finished reconciling with the provider. */
let appliedKey: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

/** Flagsmith requires the API URL to end with `/api/v1/`. */
function normalizeApiUrl(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  return raw.endsWith('/') ? raw : `${raw}/`;
}

function keyFor(user: User | null): string {
  return user === null ? 'anonymous' : user.id;
}

/** Build the OpenFeature evaluation context from the current user (or anonymous). */
function buildContext(user: User | null): EvaluationContext {
  if (user === null) {
    return { targetingKey: 'anonymous', traits: { env: APP_ENV } };
  }
  return {
    targetingKey: user.email,
    traits: {
      email: user.email,
      userId: user.id,
      fullName: user.fullName,
      tenantId: user.tenantId,
      tenantName: user.tenantName,
      env: APP_ENV,
    },
  };
}

/** Every context change, run one after another. See `applyContext`. */
let contextQueue: Promise<void> = Promise.resolve();

/**
 * Apply the user's identity + traits and wait for the provider to reconcile, then
 * publish the applied key. `OpenFeature.setContext` resolves only after the provider
 * has re-evaluated flags for the new context, so consumers gated on `appliedKey` never
 * read a stale (pre-identify) value — critical for segment/identity-level targeting.
 *
 * **Changes are queued, never run side by side.** Boot applies `anonymous` and the auth
 * restore applies the user a moment later. Run together, the anonymous call could finish
 * last and overwrite `appliedKey` while a user is signed in — every flag-guarded route
 * then read "not ready" forever and rendered blank (seen 1 load in 3, NPJB-714). Queued,
 * the last change requested is always the last one applied.
 */
function applyContext(user: User | null): Promise<void> {
  const run = async () => {
    await OpenFeature.setContext(buildContext(user));
    appliedKey = keyFor(user);
    notify();
  };
  // Run after the previous change whether it resolved or failed — one failed call must
  // not block every later login from applying.
  contextQueue = contextQueue.then(run, run);
  return contextQueue;
}

/** Push the current user's identity + traits to OpenFeature (tenant/user targeting). */
export async function setFeatureFlagContext(user: User | null): Promise<void> {
  await applyContext(user);
}

/**
 * Initialise the Flagsmith-backed OpenFeature provider once at app boot.
 * No-op (flags fall back to registry defaults) when the env key is absent.
 */
export function initFeatureFlags(): void {
  if (!ENVIRONMENT_ID) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[feature-flags] VITE_FLAGSMITH_ENVIRONMENT_ID unset — using flag defaults.');
    }
    return;
  }

  providerEnabled = true;
  const provider = new FlagsmithClientProvider({
    environmentID: ENVIRONMENT_ID,
    api: normalizeApiUrl(import.meta.env.VITE_FLAGSMITH_API_URL),
    cacheFlags: true,
  });

  OpenFeature.setProvider(provider);
  void applyContext(useAuthStore.getState().user);

  // Keep the evaluation context in sync with auth across every login/logout path.
  useAuthStore.subscribe((state, prev) => {
    if (state.user !== prev.user) {
      void applyContext(state.user);
    }
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function snapshot(): string | null {
  return appliedKey;
}

/**
 * True when feature flags are usable for the CURRENT auth state — i.e. the provider
 * has reconciled the active user's context. When Flagsmith is disabled (no env key),
 * flags resolve to registry defaults immediately, so this is always ready.
 */
export function useFeatureFlagsReady(): boolean {
  const user = useAuthStore((s) => s.user);
  const applied = useSyncExternalStore(subscribe, snapshot, snapshot);
  if (!providerEnabled) return true;
  return applied === keyFor(user);
}
